/**
 * Everything the Compact/Full-Motion choice says, and the two defaults it falls back to
 * (D-059, GDS §6/§11/§15).
 *
 * Same discipline as `camera-readiness.ts` and `calibration.ts` (D-010): the words a grown-up reads
 * on the setup screen and the value chosen for them when nobody measured anything both live in the
 * config layer, so a copy review or a playtest is a one-file change a test can audit.
 *
 * **All copy here is invented and awaiting Jon's copy table** (the D-049(5)/D-061(4)/D-063(5)
 * pattern). GDS §11 names the two setup fields — "Camera configuration" and "Jump substitute" — and
 * GDS §6/§15 name the two modes and the three substitutes, but no document contains a sentence a
 * parent reads. It is written in the parent's register, never the child's: this screen is
 * explicitly one children never see.
 */

import type { CameraMode, JumpSubstitute } from '../../shared/types/camera-mode';

/**
 * The mode a sitting gets when nothing has recommended one. **Invented, and flagged.**
 *
 * Full-Motion because it is the mode the mission is written for (GDS §6 lists cooperative play and
 * full-body jumping under it) and because Compact is framed by GDS §15 as the *accessibility*
 * path — a path a grown-up chooses, not one a piece of software puts a child on because nobody
 * stood in front of the camera. A sitting that skipped the readiness screen therefore starts here
 * and the parent may change it on the same screen they see it.
 */
export const DEFAULT_CAMERA_MODE: CameraMode = 'full-motion';

/**
 * The substitute a Compact sitting gets unless a grown-up picks another. **Invented, and flagged.**
 *
 * Large reaches, because it is the one of GDS §15's three that hero training actually measures:
 * Compact swaps calibration step 6 to `reach` (D-063(1)), so the child has demonstrated exactly
 * this movement and the threshold is their own. The other two remain one select away.
 */
export const DEFAULT_JUMP_SUBSTITUTE: JumpSubstitute = 'large-reach';

/** What each mode is called on the parent's screen, with GDS §6's distance in the parent's words. */
export const CAMERA_MODE_COPY: Readonly<
  Record<CameraMode, { readonly label: string; readonly detail: string }>
> = {
  'full-motion': {
    label: 'Full-Motion — more room',
    detail: 'About 1.5 to 2.5 metres away. Jumping, crouching and moving side to side.',
  },
  compact: {
    label: 'Compact — less room',
    detail:
      'About 1 to 1.5 metres away. Reaching and hands instead of jumping, for a small space or a child who would rather not jump.',
  },
};

/**
 * GDS §15's three substitutes, in the order the section lists them.
 *
 * One list, so the setup screen and the mid-mission override cannot offer different sets — a parent
 * who chose "alternating arm raises" at setup must be able to find it again on the pause overlay.
 */
export const JUMP_SUBSTITUTES: readonly JumpSubstitute[] = [
  'hands-raised',
  'large-reach',
  'alternating-arms',
];

/** GDS §15's three substitutes, in the parent's words. */
export const JUMP_SUBSTITUTE_COPY: Readonly<Record<JumpSubstitute, string>> = {
  'hands-raised': 'Hands raised',
  'large-reach': 'Large reaches',
  'alternating-arms': 'Alternating arm raises',
};

/**
 * Everything else the two setup fields say. **All invented.**
 *
 * Nothing here compares the two modes, describes one as better, or suggests a child is limited by
 * needing one: they are two rooms, and the words are about the room.
 */
export const INVENTED_CAMERA_SETUP_COPY = {
  /** The camera-configuration field (GDS §11 "Camera configuration"). */
  modeLegend: 'Camera play space',
  /** Shown when the camera check measured a distance and suggested a mode. */
  recommendationPrefix: 'The camera check suggests',
  /** Shown when no camera check has run this sitting. */
  noRecommendation:
    'No camera check yet this session, so this starts on Full-Motion. Run the camera check to get a suggestion.',
  /** The jump-substitute field (GDS §11 "Jump substitute"). */
  substituteLabel: 'Counting move instead of jumping',
  substituteNote:
    'In Compact play the counting rounds use this move instead of jumps. The counting itself is exactly the same.',
  /** The readiness screen's grown-up-facing recommendation line. */
  readinessRecommendation: 'Suggested play space for this room',
  /**
   * The way into the two-hero camera check (MIN §8's `Parent Setup → Hardware Readiness`).
   *
   * Only cooperative sittings see it, because only they need a readiness question this screen has
   * the answer to. Single-player readiness stays where Phase 3 put it: the Home shortcut.
   */
  coopCameraCheck: 'Check the camera for two heroes',
} as const;

/**
 * What the *stage* says about the camera, to a grown-up. **All invented.**
 *
 * Diagnostic register (the D-040(3)/D-064(5) family): it explains what the game did and what would
 * change it, and it interrupts nobody. The child is told nothing, because from where they are
 * standing nothing has changed — the keyboard has been live the whole time (CAM-09).
 */
export const INVENTED_CAMERA_STAGE_COPY = {
  /** Shown when architecture §20 refused to reuse this sitting's calibration. */
  calibrationNeededTitle: 'Hero training needed for this play space',
  calibrationNeededBody:
    'The moves this session needs were not learned in this play space, so the camera is sitting this one out. The keys still work for everything. Run hero training again from the camera check to play with the camera.',
  /** Prefixes the grown-up-facing note about what is in effect right now. */
  inEffect: 'Camera play space',
} as const;

/**
 * The two camera-era parent overrides, on the pause overlay (GDS §32, D-041(1), Slice G).
 *
 * **The two labels are GDS §32's own words** — "Restart calibration" and "Change jump substitute" —
 * exactly as the readiness rows use GDS §12's (D-066(4)). Everything else here is invented and
 * flagged, and all of it is in the grown-up's register: this panel is behind a pause, on a screen
 * §32 describes as the parent's.
 *
 * Nothing here creates negative child feedback (§32's own closing rule): no control names a
 * mistake, a failure or a child who could not do something. Restarting hero training is a thing a
 * grown-up does because a room changed, and the copy says so.
 */
export const INVENTED_CAMERA_OVERRIDE_COPY = {
  /** GDS §32's label, verbatim. Not invented. */
  restartCalibrationLabel: 'Restart calibration',
  restartCalibrationDescription:
    'Run hero training again — useful if the light, the room or where your child is standing has changed. The mission waits here.',
  /** Heading of the panel that hosts training over the paused mission. */
  restartCalibrationRunning: 'Hero training, without leaving the mission',
  /** How a parent backs out of it. */
  restartCalibrationAbandon: 'Not now',
  /** Shown once training has finished and the new measurements are waiting for Resume. */
  restartCalibrationReady:
    'The new moves are ready. Press Resume and the game will use them straight away.',
  /** Shown when a finished training still cannot serve this mission (architecture §20). */
  restartCalibrationRefused:
    'Those moves still do not cover what this mission asks for, so the keys carry on doing the work.',
  /**
   * Which hero a cooperative retraining measures (Slice F′, D-076(8), retiring D-081(8)).
   *
   * D-076(8) scopes `RESTART_CALIBRATION` per child, so in a two-hero camera sitting the control
   * has to ask. The wording names a choice, never a reason, and above all never a comparison:
   * nothing here can read as one child needing this more than the other, which is why it does not
   * mention trouble, difficulty or the camera struggling with anybody.
   *
   * **Turn-free by ruling (E-R1, D-099(5)), reworded in round 5.** This asked "Whose turn for hero
   * training?" — sequential *setup* rather than gameplay alternation, and the first draft of the
   * round-5 turn-taking sweep carried a one-sentence exemption saying so. The ruling closed that
   * route exactly as it closed the original three: the asset brief §F names turn language among the
   * phrases this product does not use, the E-R1 strings were themselves sequential-calibration
   * guidance, and a reword inside the lock costs nothing. It does not, here either — the question
   * still names the activity, still names a choice, and reads warmer without the word.
   *
   * Only heroes who are actually playing with the camera appear in the list, so this never offers
   * a control that would do nothing (the Phase 1 no-dead-buttons precedent).
   */
  chooseHeroLabel: 'Who is ready for hero training?',
  chooseHeroDescription:
    'Hero training measures one hero at a time. The other hero keeps their own moves exactly as they are, and the mission waits here for both of them.',
  /** GDS §32's label, verbatim. Not invented. */
  substituteLabel: 'Change jump substitute',
  substituteDescription:
    'Which movement the counting rounds count. The counting itself is exactly the same.',
} as const;
